import React, { useEffect, useRef, useState } from "react";
import DownloadIcon from "../../../components/icons/DownloadIcon";
import PreviewCircleIcon from "../../../components/icons/PreviewCircleIcon";
import { LocalStorageHelpers } from "../../../utils/helpers/localstorage.helpers";
import DesignTemplatePreviewRenderer from "../../../components/image.tool/DesignTemplatePreviewRenderer";
import { CommonEnums } from "../../../enums/common.enums";
import PropertyStagesDesignData from "../../../components/image.tool/property.stages.design.data";
import {
  getAddress,
  getAgencyLogoUrl,
  getCdnImageUrl,
  getDesignTemplateUrl,
} from "../../../utils/helpers/common";
import { CommonUtils } from "../../../utils/helpers/common.utils";
import PreviewModal from "./PreviewModal";
import { Modal } from "@mui/material";

/**
 * @param {Object} props
 * @param {import("../../../@types/design.template.data").DesignTemplateData} props.designTemplateData
 * @param {(item: any) => void} props.onTemplateSelect
 * @param {import("../../../@types/design.fonts").DesignFontsData} props.agencyFontsData
 * @param {boolean} [props.skipConfirmModal]
 */

const DesignTemplateListCard = ({
  designTemplateData,
  onTemplateSelect,
  agencyFontsData,
  skipConfirmModal = false,
}) => {
  const [state, setState] = useState({
    agencyData: null,
    brandData: null,
    useAgencyOfficeLogo: false,
    loadingAgencyData: true,
    imageTemplateAssets: null,
    storyTemplateAssets: null,
    imageDesignData: null,
    storyDesignData: null,
  });

  const [activeView, setActiveView] = useState("image");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [previewImages, setPreviewImages] = useState([]);

  const imagePreviewRef = useRef(null);
  const storyPreviewRef = useRef(null);
  const modalImagePreviewRef = useRef(null);
  const modalStoryPreviewRef = useRef(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const getAgencyLogo = ({ agencyData, brandData, useAgencyOfficeLogo }) => {
    if (brandData && !useAgencyOfficeLogo) {
      return getAgencyLogoUrl(brandData?.brand_logo);
    }
    return getAgencyLogoUrl(agencyData?.profile_image_ref);
  };

  useEffect(() => {
    let agencyData = LocalStorageHelpers.getAgencyData();

    if (!designTemplateData || !agencyData) return;

    let brandData = agencyData?.brand_id;
    let useAgencyOfficeLogo = agencyData?.use_office_logo ?? false;

    let imageDesignData = JSON.parse(
      JSON.stringify(
        PropertyStagesDesignData.coming_soon_property_marketing_for_sale,
      ),
    );

    imageDesignData.agency_logo = {
      url: getAgencyLogo({
        agencyData,
        brandData,
        useAgencyOfficeLogo,
      }),
      load: true,
    };
    imageDesignData.agent_name = CommonUtils.getAgentName(
      agencyData.principal_agent_id,
    );
    imageDesignData.agent_email = agencyData.principal_agent_id?.email;
    imageDesignData.agent_phone =
      agencyData.principal_agent_id?.principal_agent_mobile_number;
    imageDesignData.agent_profile_image_path = {
      url: getCdnImageUrl(
        agencyData?.principal_agent_id?.profile_image_ref?.storage_key,
      ),
      load: true,
    };
    imageDesignData.agency_trading_name = agencyData?.agency_trading_name ?? "";
    imageDesignData.agency_address = getAddress({
      unit: agencyData?.address_unit_number ?? "",
      city: agencyData?.city ?? "",
      country: agencyData?.country ?? "",
      no: agencyData?.house_number ?? "",
      postCode: agencyData?.post_code ?? "",
      state: agencyData?.state ?? "",
      streetName: agencyData?.street_name ?? "",
    });
    imageDesignData.fonts = { ...agencyFontsData };

    const getColorsData = () => {
      const data = {
        base_color_primary: { hex: "" },
        base_color_secondary: { hex: "" },
        text_color_primary: { hex: "" },
        text_color_secondary: { hex: "" },
      };

      if (brandData && !useAgencyOfficeLogo) {
        data.base_color_primary.hex = brandData?.base_color_primary?.hex ?? "";
        data.base_color_secondary.hex =
          brandData?.base_color_secondary?.hex ?? "";
        data.text_color_primary.hex = brandData?.text_color_primary?.hex ?? "";
        data.text_color_secondary.hex =
          brandData?.text_color_secondary?.hex ?? "";

        return data;
      }

      data.base_color_primary.hex = agencyData?.base_color_primary?.hex ?? "";
      data.base_color_secondary.hex =
        agencyData?.base_color_secondary?.hex ?? "";
      data.text_color_primary.hex = agencyData?.text_color_primary?.hex ?? "";
      data.text_color_secondary.hex =
        agencyData?.text_color_secondary?.hex ?? "";

      return data;
    };

    const updatedColors = getColorsData();

    imageDesignData = {
      ...imageDesignData,
      base_color_primary: {
        r: 255,
        g: 0,
        b: 0,
        hex: updatedColors.base_color_primary?.hex,
      },
      base_color_secondary: {
        r: 255,
        g: 255,
        b: 255,
        hex: updatedColors?.base_color_secondary?.hex,
      },
      text_color_primary: {
        r: 255,
        g: 255,
        b: 255,
        hex: updatedColors?.text_color_primary?.hex,
      },
      text_color_secondary: {
        r: 255,
        g: 0,
        b: 0,
        hex: updatedColors?.text_color_secondary?.hex,
      },
    };

    let storyDesignData = JSON.parse(JSON.stringify(imageDesignData));

    let imageTemplateUrl = getDesignTemplateUrl(
      designTemplateData.image_post_master_design_svg_file.upload_record_id,
    );
    let storyTemplateUrl = getDesignTemplateUrl(
      designTemplateData.story_post_master_design_svg_file.upload_record_id,
    );
    let imageTemplateAssets =
      designTemplateData.image_post_master_design_assets;
    let storyTemplateAssets =
      designTemplateData.story_post_master_design_assets;

    imageDesignData.post_type = "image_post";
    storyDesignData.post_type = "story_post";

    imageDesignData.template_url = {
      url: imageTemplateUrl,
      load: true,
    };
    storyDesignData.template_url = {
      url: storyTemplateUrl,
      load: true,
    };

    setState((prev) => ({
      ...prev,
      agencyData,
      brandData,
      useAgencyOfficeLogo: useAgencyOfficeLogo,
      loadingAgencyData: false,
      imageTemplateAssets: imageTemplateAssets,
      storyTemplateAssets: storyTemplateAssets,
      imageDesignData,
      storyDesignData,
    }));
  }, [designTemplateData, agencyFontsData]);

  if (state.loadingAgencyData) {
    return <p>Loading agency data...</p>;
  }

  const handleToggleView = () => {
    setActiveView((prev) => (prev === "image" ? "story" : "image"));
  };

  const getCanvasImageFromRef = (wrapperRef) => {
    const root = wrapperRef?.current;
    if (!root) return null;

    const canvas =
      root.querySelector("canvas.lower-canvas") || root.querySelector("canvas");

    if (!canvas) return null;

    try {
      return canvas.toDataURL("image/png");
    } catch (e) {
      console.error("Canvas export failed:", e);
      return null;
    }
  };

  const downloadDataUrl = (dataUrl, typeLabel) => {
    if (!dataUrl) return;

    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `${designTemplateData?.name || "template"}-${typeLabel}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = () => {
    const dataUrl =
      activeView === "image"
        ? getCanvasImageFromRef(imagePreviewRef)
        : getCanvasImageFromRef(storyPreviewRef);

    downloadDataUrl(
      dataUrl,
      activeView === "image" ? "image-post" : "story-post",
    );
  };

  const handleOpenPreview = () => {
    const dataUrl =
      activeView === "image"
        ? getCanvasImageFromRef(imagePreviewRef)
        : getCanvasImageFromRef(storyPreviewRef);

    if (!dataUrl) return;

    setPreviewImages([dataUrl]);
    setIsModalOpen(true);
  };

  const handleModalDownload = (type) => {
    const dataUrl =
      type === "image"
        ? getCanvasImageFromRef(modalImagePreviewRef)
        : getCanvasImageFromRef(modalStoryPreviewRef);

    downloadDataUrl(dataUrl, type === "image" ? "image-post" : "story-post");
  };

  const handleModalPreview = (type) => {
    const dataUrl =
      type === "image"
        ? getCanvasImageFromRef(modalImagePreviewRef)
        : getCanvasImageFromRef(modalStoryPreviewRef);

    if (!dataUrl) return;

    setPreviewImages([dataUrl]);
    setIsModalOpen(true);
  };

const handleConfirmMasterDesign = async () => {
  if (!onTemplateSelect || isConfirming) return;

  try {
    setIsConfirming(true);
    await onTemplateSelect(designTemplateData);
    
    setShowConfirmModal(false); 
  } catch (e) {
    console.error("Selection failed", e);
  } finally {
    setIsConfirming(false);
  }
};

  console.log("PARENT templateData:", designTemplateData);

  return (
    <div
      className="design-template-card-wrapper design-templates-list-card"
      key={designTemplateData?._id}
    >
      <div className="template-header-bar">
        <p className="template-name">{designTemplateData?.name}</p>
        <button
          className={`select-btn ${designTemplateData?.selected ? "selected" : ""}`}
          disabled={designTemplateData?.selected}
          onClick={() => {
            if (designTemplateData?.selected) return;
            if (skipConfirmModal) {
              onTemplateSelect(designTemplateData);
              return;
            }
            setShowConfirmModal(true);
          }}
        >
          {designTemplateData?.selected ? "Selected" : "Select"}
        </button>
      </div>

      <div className="single-preview-layout">
        <div className="preview-main-box">
          {activeView === "image" ? (
            <div className="image-view-wrapper" ref={imagePreviewRef}>
              <DesignTemplatePreviewRenderer
                key="main-image-preview"
                agencyData={state.agencyData}
                brandData={state.brandData}
                designStage={
                  CommonEnums.designStageTypes
                    .coming_soon_property_marketing_for_sale
                }
                useAgencyOfficeLogo={state.useAgencyOfficeLogo}
                designDataFromParent={state.imageDesignData}
                canvasWidth={1080}
                canvasHeight={1080}
                designTemplateAssets={state.imageTemplateAssets}
                templateData={designTemplateData}
              />
            </div>
          ) : (
            <div
              className="story-view-wrapper story-design-template-preview-container"
              ref={storyPreviewRef}
            >
              <DesignTemplatePreviewRenderer
                key="main-story-preview"
                agencyData={state.agencyData}
                brandData={state.brandData}
                designStage={
                  CommonEnums.designStageTypes
                    .coming_soon_property_marketing_for_sale
                }
                useAgencyOfficeLogo={state.useAgencyOfficeLogo}
                designDataFromParent={state.storyDesignData}
                canvasWidth={1080}
                canvasHeight={1920}
                designTemplateAssets={state.storyTemplateAssets}
                templateData={designTemplateData}
              />
            </div>
          )}

          <button
            type="button"
            className={`view-toggle-btn ${
              activeView === "image" ? "toggle-to-story" : "toggle-to-image"
            }`}
            onClick={handleToggleView}
            aria-label="Toggle image/story preview"
          >
            <img src="/images/workspace/image.svg" alt="Switch view" />
          </button>
        </div>

        <div className="post-footer">
          <span className="post-type-label">
            {activeView === "image" ? "Image Post" : "Story Post"}
          </span>
          <div className="footer-icons">
            <span
              className="footer-icon-btn"
              role="button"
              tabIndex={0}
              onClick={handleDownload}
            >
              <DownloadIcon />
            </span>
            <span
              className="footer-icon-btn"
              role="button"
              tabIndex={0}
              onClick={handleOpenPreview}
            >
              <PreviewCircleIcon />
            </span>
          </div>
        </div>
      </div>

      <div style={{ position: "relative", zIndex: 10000 }}>
        <PreviewModal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setPreviewImages([]);
          }}
          images={previewImages}
        />
      </div>

      <Modal
        open={showConfirmModal}
        onClose={() => {
          if (isConfirming) return;
          setShowConfirmModal(false);
        }}
        className="master-design-modal-wrapper"
      >
        <div className="master-confirm-container">
          <h3>Set as Master Design?</h3>
          <p className="modal-subtitle">
            All future marketing assets will be generated based on this master
            design setup. You can customize these designs at any time after
            confirmation.
          </p>

          <div className="modal-preview-section">
            <div className="template-header-bar mini">
              <p className="template-name">{designTemplateData?.name}</p>
            </div>

            <div className="template-preview-row">
              <div className="post-column">
                <div
                  className="image-post-box design-templates-list-card"
                  ref={modalImagePreviewRef}
                >
                  <DesignTemplatePreviewRenderer
                    key="modal-image-preview"
                    agencyData={state.agencyData}
                    brandData={state.brandData}
                    designStage={
                      CommonEnums.designStageTypes
                        .coming_soon_property_marketing_for_sale
                    }
                    useAgencyOfficeLogo={state.useAgencyOfficeLogo}
                    designDataFromParent={state.imageDesignData}
                    canvasWidth={1080}
                    canvasHeight={1080}
                    templateData={designTemplateData}
                  />
                </div>
                <div className="post-footer">
                  <span>Image Post</span>
                  <div className="footer-icons">
                    <span
                      className="footer-icon-btn"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleModalDownload("image")}
                    >
                      <DownloadIcon />
                    </span>
                    <span
                      className="footer-icon-btn"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleModalPreview("image")}
                    >
                      <PreviewCircleIcon />
                    </span>
                  </div>
                </div>
              </div>

              <div className="post-column">
                <div className="story-post-box" ref={modalStoryPreviewRef}>
                  <div className="story-inner-img story-design-template-preview-container">
                    {state.storyDesignData && (
                      <DesignTemplatePreviewRenderer
                        key="modal-story-preview"
                        agencyData={state.agencyData}
                        brandData={state.brandData}
                        designStage={
                          CommonEnums.designStageTypes
                            .coming_soon_property_marketing_for_sale
                        }
                        useAgencyOfficeLogo={state.useAgencyOfficeLogo}
                        designDataFromParent={state.storyDesignData}
                        canvasWidth={1080}
                        canvasHeight={1920}
                        templateData={designTemplateData}
                      />
                    )}
                  </div>
                </div>
                <div className="post-footer">
                  <span>Story Post</span>
                  <div className="footer-icons">
                    <span
                      className="footer-icon-btn"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleModalDownload("story")}
                    >
                      <DownloadIcon />
                    </span>
                    <span
                      className="footer-icon-btn"
                      role="button"
                      tabIndex={0}
                      onClick={() => handleModalPreview("story")}
                    >
                      <PreviewCircleIcon />
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="final-confirmation-text">
            Would you like to set this as the master design?
          </p>

          <div className="modal-action-btns">
            <button
              type="button"
              className="btn-cancel"
              disabled={isConfirming}
              onClick={() => setShowConfirmModal(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-confirm"
              disabled={isConfirming}
              onClick={handleConfirmMasterDesign}
            >
              {isConfirming ? "Saving..." : "Confirm"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default React.memo(DesignTemplateListCard);
